/*
 * plp-visual-nav.js — <plp-visual-nav> (snippets/plp-visual-nav.liquid).
 *
 * The row itself is native scroll-snap; this runs tenthousand's pager under
 * it: a scrollbar-style progress thumb (width = visible share, position =
 * scroll progress) and "< 1/N >", where N counts the row's scroll stops (each
 * card's snap position, the last one clamped to the end of the row) and the
 * arrows step one stop. data-overflow reflects a real measurement and
 * overrides the snippet's server-side guess at whether the pager shows.
 */

if (!customElements.get('plp-visual-nav')) {
  customElements.define(
    'plp-visual-nav',
    class PlpVisualNav extends HTMLElement {
      connectedCallback() {
        if (this.__pvnInit) return;
        this.__pvnInit = true;
        this.track = this.querySelector('.pvn__track');
        this.items = Array.from(this.querySelectorAll('.pvn__item'));
        this.fill = this.querySelector('.pvn__progress-fill');
        this.counter = this.querySelector('.pvn__counter');
        this.prev = this.querySelector('.pvn__arrow--prev');
        this.next = this.querySelector('.pvn__arrow--next');
        if (!this.track || !this.items.length) return;

        this.stops = [0];
        this.index = 0;
        this.pending = null;
        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', () => window.requestAnimationFrame(this.update), { passive: true });
        // step from where a running arrow scroll is heading, so quick repeat
        // clicks advance instead of re-targeting the same card
        this.prev?.addEventListener('click', () => this.go((this.pending ?? this.index) - 1));
        this.next?.addEventListener('click', () => this.go((this.pending ?? this.index) + 1));
        // a swipe or wheel takes over from an arrow scroll in flight
        const release = () => (this.pending = null);
        this.track.addEventListener('pointerdown', release, { passive: true });
        this.track.addEventListener('wheel', release, { passive: true });
        this.resizeObserver = new ResizeObserver(() => {
          this.measure();
          this.update();
        });
        this.resizeObserver.observe(this.track);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.__pvnInit = false;
      }

      measure() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.max = max;
        this.dataset.overflow = max > 1 ? 'true' : 'false';
        // offsets are relative to the track (position: relative), so they
        // don't move as it scrolls; card 1's offset is the scroll origin
        const origin = this.items[0].offsetLeft;
        const stops = [];
        for (const item of this.items) {
          const stop = Math.min(max, item.offsetLeft - origin);
          if (!stops.length || stop - stops[stops.length - 1] > 1) stops.push(stop);
          if (stop >= max) break;
        }
        this.stops = stops.length ? stops : [0];
      }

      update() {
        if (!(this.max > 1)) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.track;

        const share = clientWidth / scrollWidth;
        const progress = Math.min(1, Math.max(0, scrollLeft / this.max));
        if (this.fill) {
          this.fill.style.width = share * 100 + '%';
          this.fill.style.transform = 'translateX(' + (progress * (1 - share) * 100) / share + '%)';
        }

        let index = 0;
        this.stops.forEach((stop, i) => {
          if (Math.abs(stop - scrollLeft) < Math.abs(this.stops[index] - scrollLeft)) index = i;
        });
        this.index = index;
        if (this.pending === index) this.pending = null;
        const heading = this.pending ?? index;
        if (this.counter) this.counter.textContent = index + 1 + '/' + this.stops.length;
        if (this.prev) this.prev.disabled = heading === 0;
        if (this.next) this.next.disabled = heading === this.stops.length - 1;
      }

      go(index) {
        this.pending = Math.max(0, Math.min(this.stops.length - 1, index));
        this.track.scrollTo({ left: this.stops[this.pending], behavior: 'smooth' });
      }
    }
  );
}
